import { FC, memo } from 'react';

import { TEXTS } from 'application/pages/texts';
import { TEXTS as POINT_TEXTS } from 'application/pages/PointsPage/renderer/texts';
import { HELP_LINK_SLEEP_SETTINGS } from 'application/pages/constants';
import { UseDebouncedSuggestionAddressType } from 'application/hooks/useAddressState';
import { ModalProps, ModalTypes } from 'application/providers/ModalProvider';
import { useAccountCompanyCountry } from 'application/hooks';
import {
  DEFAULT_START_TIME,
  DEFAULT_END_TIME,
} from 'application/pages/PointsPage/renderer/PointsDrawer/PointsDrawerContent/SleepSettings/SleepSettingsEditBlock/consts';

import { Country } from '@shared/api/graphql/generated';

import Autocomplete from '@zvuk-b2b/react-uikit/ui/Autocomplete';
import { DropdownPlacement } from '@zvuk-b2b/react-uikit/ui/Dropdown';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import Info, { InfoTypes } from '@zvuk-b2b/react-uikit/ui/Info';
import TimeInput, { defaultTimeInputValidator, TIME_PATTERN } from '@zvuk-b2b/react-uikit/ui/TimeInput';

import styles from './index.module.scss';

interface ModalContentProps {
  isAcceptanceAdvancedNull: boolean;
  isAcceptanceAdvancedPending: boolean;
  addressState: UseDebouncedSuggestionAddressType;
  createPointError?: Error;
  isShowHintAboutPrice?: boolean;
  withResidualPayment: boolean;
  amount?: number;
  period?: string;
  fromTime: string;
  toTime: string;
  onFromTimeChange: (value: string) => void;
  onToTimeChange: (value: string) => void;
  onFromTimeBlur: () => void;
  onToTimeBlur: () => void;
  onAutocompleteFullModeChange?: (isFullMode: boolean) => void;
  openModal: ((type: ModalTypes, props: {}) => void) &
    (<T extends ModalTypes>(
      type: T,
      modalState: Omit<ModalProps[T], 'close' | 'open' | 'openModal' | 'replaceModal'>,
      onModalCloseCallback?: () => void
    ) => void);
}

const getPlaceholder = (companyCountry?: Country) => {
  switch (companyCountry) {
    case Country.By:
      return TEXTS.POINT_CREATE_ADDRESS_PLACEHOLDER_BY;
    case Country.Kz:
      return TEXTS.POINT_CREATE_ADDRESS_PLACEHOLDER_KZ;
    default:
      return TEXTS.POINT_CREATE_ADDRESS_PLACEHOLDER_RU;
  }
};

const ModalContent: FC<ModalContentProps> = ({
  isAcceptanceAdvancedNull,
  isAcceptanceAdvancedPending,
  addressState,
  createPointError,
  isShowHintAboutPrice,
  withResidualPayment,
  amount = 0,
  period = '',
  fromTime,
  toTime,
  onFromTimeChange,
  onToTimeChange,
  onFromTimeBlur,
  onToTimeBlur,
  onAutocompleteFullModeChange,
  openModal,
}) => {
  const companyCountry = useAccountCompanyCountry();

  const onPointCreateHintModalShow = () => {
    openModal(ModalTypes.POINT_CREATE_HINT, {});
  };

  const onSleepSettingsHintClick = () => {
    window.open(HELP_LINK_SLEEP_SETTINGS, '_blank', 'noreferrer');
  };

  if (isAcceptanceAdvancedNull) {
    return <div className={styles.contentSkeleton} />;
  }

  return (
    <>
      <Autocomplete
        elementId="input_create_point_modal_address"
        autoComplete="off"
        label={POINT_TEXTS.POINT_CREATE_ADDRESS}
        placeholder={getPlaceholder(companyCountry)}
        value={addressState.value}
        options={addressState.options || []}
        error={createPointError?.message}
        isLoading={addressState.loading}
        onChange={addressState.onChange}
        dropdownPlacement={DropdownPlacement.BOTTOM}
        onFullModeChange={onAutocompleteFullModeChange}
      />

      <Button
        elementId="button_show_create_point_with_payment_hint_modal"
        type={ButtonType.PLAIN}
        className={styles.hintButton}
        onClick={onPointCreateHintModalShow}>
        {TEXTS.POINT_CREATE_HINT_MODAL_TITLE}
      </Button>

      <div className={styles.scheduleContainer}>
        <TimeInput
          elementId="input_create_point_modal_from_time"
          value={fromTime}
          onChange={onFromTimeChange}
          onBlur={onFromTimeBlur}
          label={POINT_TEXTS.SLEEP_SETTINGS_TITLE}
          defaultValue={DEFAULT_START_TIME}
          inputValidator={defaultTimeInputValidator}
          pattern={TIME_PATTERN}
          mask="час:мин"
          placeholder="00:00"
        />
        <TimeInput
          elementId="input_create_point_modal_to_time"
          value={toTime}
          onChange={onToTimeChange}
          onBlur={onToTimeBlur}
          defaultValue={DEFAULT_END_TIME}
          inputValidator={defaultTimeInputValidator}
          pattern={TIME_PATTERN}
          mask="час:мин"
          placeholder="00:00"
        />
      </div>

      <Button
        elementId="button_show_sleep_settings_hint"
        type={ButtonType.PLAIN}
        className={styles.hintButton}
        onClick={onSleepSettingsHintClick}>
        {TEXTS.SLEEP_SETTINGS_HINT_LINK}
      </Button>

      {isShowHintAboutPrice && !isAcceptanceAdvancedPending && (
        <Info className={styles.hintInfo}>
          {withResidualPayment ? (
            <div className={styles.hintInfoResidual}>
              <div>{POINT_TEXTS.POINT_CREATE_HINT_COST_MODAL_RESIDUAL_TITLE}</div>
              <div>
                <div>{POINT_TEXTS.POINT_CREATE_HINT_COST_MODAL_RESIDUAL_DESCRIPTION}</div>
                <div>{POINT_TEXTS.POINT_CREATE_HINT_COST_MODAL_RESIDUAL_RESIDUAL_PRICE(amount, period)}</div>
              </div>
            </div>
          ) : (
            POINT_TEXTS.POINT_CREATE_HINT_COST_MODAL_TITLE
          )}
        </Info>
      )}

      {isAcceptanceAdvancedPending && (
        <Info
          type={InfoTypes.WARNING}
          className={styles.hintInfo}>
          <div>{POINT_TEXTS.POINT_CREATE_HINT_ACCEPTANCE_ADVANCED_1}</div>
          <div>{POINT_TEXTS.POINT_CREATE_HINT_ACCEPTANCE_ADVANCED_2}</div>
        </Info>
      )}
    </>
  );
};

export default memo(ModalContent);
